import { devtools } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';
import { MAPBOX_CONFIG } from '~/constants/mapbox';
import {
  UserAction,
  RangePickerValuesType,
  NestedHome,
  NestedProject,
  NestedSearch,
  TODO,
  Broker,
  StatusType,
} from '@habity/types';
import type { AutocompleteItem } from '@habity/types/autocomplete';

type Modals = {
  /** Simple login modal - just for logging in, no action context */
  loginOpen: boolean;
  enhancedLoginOpen: {
    isOpen: boolean;
    home?: NestedHome | null;
    project?: NestedProject | null;
    action?: UserAction | null;
    redirectTo?: string;
    searchFilters?: Partial<NestedSearch>;
    preventClose?: boolean;
  };
  actionConfirmationOpen: {
    isOpen: boolean;
    home?: NestedHome | null;
    project?: NestedProject | null;
    action?: UserAction | null;
    email: string;
  };
  actionLoading: boolean;
  searchOpen: boolean;
  completionModalOpen: boolean;
  addressOpen: boolean;
  feedbackOpen: boolean;
  providerDataOpen: boolean;
  calendarOpen: boolean;
  notifyBuyersOpen: boolean;
  profileCompletionOpen: boolean;
  buyersCollapseOpen: boolean;
  desktopMenuOpen: boolean;
  mobileMenuOpen: boolean;
  bankIdOpen: boolean;
  propertySearchOpen: boolean;
  clientPortalOpen: boolean;
};

type Loading = {
  isLoading: boolean;
  homesLoading: boolean;
  scrollLoading: boolean;
  staticHomesLoading: boolean;
  homesCountLoading: boolean;
  homesCoordinatesLoading: boolean;
  completionLoading: boolean;
  resultsLoading: boolean;
};

export type ScopeType = 'homes' | 'searches' | 'account';

export type Scope = {
  label: string;
  id: string | null;
  type: ScopeType;
};

export interface UIStore {
  scope: Scope | null;
  setScope: (scope: Scope | null) => void;
  loginViaViewingSlotId: number | undefined;
  setLoginViaViewingSlotId: (slotId: number | undefined) => void;
  mapViewState: TODO;
  setMapViewState: (mapViewState: TODO) => void;
  rangePickerValues: RangePickerValuesType | null;
  setRangePickerValues: (
    rangePickerValues: RangePickerValuesType | null
  ) => void;
  scrollPosition: number;
  setScrollPosition: (scrollPosition: number) => void;
  currentTab: number;
  setCurrentTab: (currentTab: number) => void;
  loading: Loading;
  setLoading: (loading: Loading) => void;
  modals: Modals;
  setModals: (modals: Modals) => void;
  searchJustCreated: boolean;
  setSearchJustCreated: (value: boolean) => void;
  lastCreatedSearchId: number | null;
  setLastCreatedSearchId: (id: number | null) => void;
  editSearchId: number | null;
  setEditSearchId: (id: number | null) => void;
  createSearchLocations: AutocompleteItem[] | null;
  setCreateSearchLocations: (locations: AutocompleteItem[] | null) => void;
  useLegacyProfile: boolean;
  setUseLegacyProfile: (useLegacyProfile: boolean) => void;
  showMatchBuyersDrawer: boolean;
  setShowMatchBuyersDrawer: (value: boolean) => void;
  showProfileCompletionModal: (userData?: TODO | null) => void;
  profileData: {
    userData: TODO | null;
    isLoading: boolean;
  };
  setProfileData: (userData: TODO | null) => void;
  brokerProviderDataModalBroker: Broker | null;
  openBrokerProviderDataModal: (broker: Broker) => void;
  closeBrokerProviderDataModal: () => void;
  /** Simple function to open the login modal (no action, just login) */
  openLoginModal: () => void;
  sellModalSearchText: string | undefined;
  setSellModalSearchText: (text: string | undefined) => void;
}

// FIXME: Cleanup
export const useUIStore = createWithEqualityFn<UIStore>()(
  devtools((set) => ({
    scope: null,
    setScope: (scope) => set({ scope }),
    loginViaViewingSlotId: undefined,
    setLoginViaViewingSlotId: (loginViaViewingSlotId) => set({ loginViaViewingSlotId }),
    mapViewState: MAPBOX_CONFIG.INITIAL_VIEW_STATE,
    setMapViewState: (mapViewState) => set({ mapViewState }),
    rangePickerValues: null,
    setRangePickerValues: (rangePickerValues) => set({ rangePickerValues }),
    scrollPosition: 0,
    setScrollPosition: (scrollPosition) => set({ scrollPosition }),
    currentTab: 0,
    setCurrentTab: (currentTab) => set({ currentTab }),
    modals: {
      loginOpen: false,
      enhancedLoginOpen: {
        isOpen: false,
        home: null,
        action: null,
        redirectTo: undefined,
      },
      actionConfirmationOpen: {
        isOpen: false,
        home: null,
        project: null,
        action: null,
        email: '',
      },
      actionLoading: false,
      searchOpen: false,
      completionModalOpen: false,
      addressOpen: false,
      feedbackOpen: true,
      buyersCollapseOpen: false,
      providerDataOpen: false,
      calendarOpen: false,
      notifyBuyersOpen: false,
      profileCompletionOpen: false,
      desktopMenuOpen: false,
      mobileMenuOpen: false,
      bankIdOpen: false,
      propertySearchOpen: false,
      clientPortalOpen: false,
    },
    profileData: {
      userData: null,
      isLoading: false,
    },
    setModals: (modals) => {
      set({ modals });
    },
    searchJustCreated: false,
    setSearchJustCreated: (value) => set({ searchJustCreated: value }),
    lastCreatedSearchId: null,
    setLastCreatedSearchId: (id) => set({ lastCreatedSearchId: id }),
    editSearchId: null,
    setEditSearchId: (id) => set({ editSearchId: id }),
    createSearchLocations: null,
    setCreateSearchLocations: (locations) => set({ createSearchLocations: locations }),
    showProfileCompletionModal: (userData = null) =>
      set((state) => ({
        modals: {
          ...state.modals,
          profileCompletionOpen: true,
        },
        profileData: {
          ...state.profileData,
          userData: userData || state.profileData.userData,
        },
      })),

    setProfileData: (userData) =>
      set((state) => ({
        profileData: {
          ...state.profileData,
          userData,
          isLoading: false,
        },
      })),

    loading: {
      isLoading: false,
      homesLoading: false,
      scrollLoading: false,
      staticHomesLoading: false,
      homesCountLoading: false,
      homesCoordinatesLoading: false,
      completionLoading: false,
      resultsLoading: false,
    },
    setLoading: (loading) => {
      set({ loading });
    },
    useLegacyProfile: false,
    setUseLegacyProfile: (useLegacyProfile) => set({ useLegacyProfile }),
    showMatchBuyersDrawer: false,
    setShowMatchBuyersDrawer: (value) => set({ showMatchBuyersDrawer: value }),

    // Broker provider data modal functionality
    brokerProviderDataModalBroker: null,
    openBrokerProviderDataModal: (broker) =>
      set((state) => ({
        brokerProviderDataModalBroker: broker,
        modals: {
          ...state.modals,
          providerDataOpen: true,
        },
      })),
    closeBrokerProviderDataModal: () =>
      set((state) => ({
        brokerProviderDataModalBroker: null,
        modals: {
          ...state.modals,
          providerDataOpen: false,
        },
      })),

    // Simple login modal opener (no action, just login)
    openLoginModal: () =>
      set((state) => ({
        modals: {
          ...state.modals,
          loginOpen: true,
        },
      })),

    sellModalSearchText: undefined,
    setSellModalSearchText: (text) => set({ sellModalSearchText: text }),
  })),
  shallow
);
